import React, { useEffect, useState, useRef } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import { useToast } from '../contexts/ToastContext';

export default function BattlefieldManagement() {
  const [myBattlefields, setMyBattlefields] = useState([]);
  const [forSaleBattlefields, setForSaleBattlefields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingForSale, setLoadingForSale] = useState(true);
  const [upgrading, setUpgrading] = useState({});
  const [activeTab, setActiveTab] = useState('for-sale');
  const [selectedBattlefield, setSelectedBattlefield] = useState(null);
  const [battlefieldView, setBattlefieldView] = useState('list'); // 'list' or 'detail'
  const [battlefieldDetailTab, setBattlefieldDetailTab] = useState('overview');
  const [battlefieldLoot, setBattlefieldLoot] = useState(null);
  const [battlefieldLootPool, setBattlefieldLootPool] = useState([]);
  const [battlefieldMusic, setBattlefieldMusic] = useState([]);
  const [battlefieldTokenBalance, setBattlefieldTokenBalance] = useState(0);
  const [soulwarsBalance, setSoulwarsBalance] = useState(0);
  const [userInventory, setUserInventory] = useState([]);
  const [tokenBalances, setTokenBalances] = useState({});
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [selectedLootPoolItem, setSelectedLootPoolItem] = useState(null);
  const [selectedLootPoolToken, setSelectedLootPoolToken] = useState(null);
  const [battlefieldLootSummaries, setBattlefieldLootSummaries] = useState({});
  const { user, getAuthHeaders, isAuthenticated } = useJWTAuth();
  const { warning, success } = useToast();

  const loadMyBattlefields = async () => {
    if (!isAuthenticated() || !user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const currentUserId = user.id;
      const res = await fetch(`/api/battlefields/user/${currentUserId}`, { 
        headers: getAuthHeaders() 
      });
      const json = await res.json();
      
      if (json.success) {
        const battlefields = json.data || [];
        setMyBattlefields(battlefields);
        
        // Load loot summaries for all owned battlefields
        if (battlefields.length > 0) {
          battlefields.forEach(bf => {
            loadBattlefieldLootSummary(bf.id);
          });
        }
      } else {
        console.error('Failed to load battlefields:', json.error);
      }
    } catch (error) {
      console.error('Error loading battlefields:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadForSaleBattlefields = async () => {
    try {
      setLoadingForSale(true);
      const res = await fetch('/api/battlefields/for-sale', {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });
      const json = await res.json();
      
      if (json.success) {
        setForSaleBattlefields((json.data || []).slice(0, 10));
      } else {
        console.error('Failed to load battlefields for sale:', json.error);
      }
    } catch (error) {
      console.error('Error loading battlefields for sale:', error);
    } finally {
      setLoadingForSale(false);
    }
  };

  const loadBalances = async () => {
    if (!isAuthenticated() || !user?.id) return;
    
    try {
      // Load all token balances from ledger
      const balanceRes = await fetch('/api/ledger/balance', { headers: getAuthHeaders() });
      const balanceJson = await balanceRes.json();
      
      console.log('[BattlefieldManagement] Balance response:', balanceJson);
      
      if (balanceJson.data) {
        // Extract $SOULWARS balance (check both lowercase and any case variations)
        const solanaBalances = balanceJson.data.solana || {};
        const soulwarsKey = Object.keys(solanaBalances).find(k => k.toLowerCase() === 'soulwars');
        if (soulwarsKey) {
          setSoulwarsBalance(Math.round(parseFloat(solanaBalances[soulwarsKey]) || 0));
        } else {
          setSoulwarsBalance(0);
        }
        
        // Extract Battlefield token balance (check both uppercase and any case variations)
        const virtualBalances = balanceJson.data.virtual || {};
        const battlefieldKey = Object.keys(virtualBalances).find(k => k.toUpperCase() === 'BATTLEFIELD');
        if (battlefieldKey) {
          setBattlefieldTokenBalance(parseFloat(virtualBalances[battlefieldKey]) || 0);
        } else {
          setBattlefieldTokenBalance(0);
        }
        
        // Store all token balances for loot pool selection
        const allBalances = {};
        Object.keys(balanceJson.data).forEach(chain => {
          Object.keys(balanceJson.data[chain] || {}).forEach(tokenSymbol => {
            allBalances[tokenSymbol] = parseFloat(balanceJson.data[chain][tokenSymbol]) || 0;
          });
        });
        setTokenBalances(allBalances);
      }
    } catch (error) {
      console.error('Error loading balances:', error);
    }
  };

  const loadUserInventory = async () => {
    if (!isAuthenticated() || !user?.id) return;
    
    try {
      setLoadingInventory(true);
      const res = await fetch('/api/inventory/user', { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setUserInventory(json.inventory || []);
      }
    } catch (error) {
      console.error('Error loading user inventory:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const loadBattlefieldLoot = async (battlefieldId) => {
    try {
      const res = await fetch(`/api/battlefields/${battlefieldId}/loot`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (json.success) {
        setBattlefieldLoot(json.data);
      }
    } catch (error) {
      console.error('Error loading battlefield loot:', error);
    }
  };

  const loadBattlefieldLootSummary = async (battlefieldId) => {
    try {
      const res = await fetch(`/api/battlefields/${battlefieldId}/loot`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (json.success && json.data.summary) {
        setBattlefieldLootSummaries(prev => ({
          ...prev,
          [battlefieldId]: json.data.summary
        }));
      }
    } catch (error) {
      console.error('Error loading battlefield loot summary:', error);
    }
  };

  const loadBattlefieldLootPool = async (battlefieldId) => {
    try {
      const res = await fetch(`/api/battlefields/${battlefieldId}/loot-pool`, {
        headers: getAuthHeaders()
      });
      const json = await res.json();
      if (json.success) {
        setBattlefieldLootPool(json.data || []);
      }
    } catch (error) {
      console.error('Error loading battlefield loot pool:', error);
    }
  };

  const loadBattlefieldMusic = async (battlefieldId) => {
    try {
      // Get theme from battlefield
      const bfRes = await fetch(`/api/battlefields/${battlefieldId}`, {
        headers: getAuthHeaders()
      });
      const bfJson = await bfRes.json();
      if (bfJson.success && bfJson.data.themeBattlefield) {
        const themeId = bfJson.data.themeBattlefield.theme || 'soulwars-default';
        // Load music from theme
        const musicRes = await fetch(`/api/themes/${themeId}/music?category=battle`);
        const musicJson = await musicRes.json();
        if (musicJson.success) {
          setBattlefieldMusic(musicJson.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading battlefield music:', error);
    }
  };

  useEffect(() => {
    loadForSaleBattlefields();
    if (isAuthenticated()) {
      loadMyBattlefields();
      loadBalances();
    }
  }, [user, isAuthenticated()]);

  useEffect(() => {
    if (selectedBattlefield && battlefieldView === 'detail') {
      if (battlefieldDetailTab === 'loot') {
        loadBattlefieldLoot(selectedBattlefield.id);
      } else if (battlefieldDetailTab === 'loot-pool') {
        loadBattlefieldLootPool(selectedBattlefield.id);
        loadBattlefieldMusic(selectedBattlefield.id);
        loadUserInventory();
        loadBalances();
      }
    }
  }, [selectedBattlefield, battlefieldView, battlefieldDetailTab]);

  const upgradeEfficiency = async (battlefieldId, upgradeType) => {
    try {
      setUpgrading({ ...upgrading, [`${battlefieldId}-${upgradeType}`]: true });
      
      const res = await fetch(`/api/battlefields/${battlefieldId}/upgrade-loot-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ upgradeType })
      });

      const json = await res.json();
      
      if (json.success) {
        success(`Upgrade successful! Cost: ${json.data.cost.toFixed(2)} $SOULWARS`);
        await loadMyBattlefields();
        await loadBalances();
        if (selectedBattlefield?.id === battlefieldId) {
          const updated = myBattlefields.find(bf => bf.id === battlefieldId);
          if (updated) setSelectedBattlefield(updated);
        }
      } else {
        warning(`Upgrade failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error upgrading efficiency:', error);
      warning('Error upgrading efficiency');
    } finally {
      setUpgrading({ ...upgrading, [`${battlefieldId}-${upgradeType}`]: false });
    }
  };

  const calculateUpgradeCost = (currentLevel) => {
    const baseCost = 100;
    const newLevel = Math.min(100.00, currentLevel + 1.00);
    return baseCost * Math.pow(newLevel, 1.5);
  };

  const formatEfficiency = (value) => {
    return typeof value === 'number' ? Math.round(value) + '%' : '0%';
  };

  const formatBattlefieldImage = (imagePath, themeId = null) => {
    if (!imagePath) return null;
    
    // If already a full URL, return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Check if it's an S3 path (for custom themes)
    const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';
    const awsRegion = process.env.REACT_APP_AWS_APP_REGION || 'us-east-1';
    const resolvedTheme = themeId || DEFAULT_THEME_ID;
    
    // If theme is not default, try S3 path
    if (resolvedTheme !== DEFAULT_THEME_ID) {
      const imagePathFormatted = imagePath.startsWith('battlefields/')
        ? imagePath
        : `battlefields/${imagePath}`;
      return `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${resolvedTheme}/${imagePathFormatted}`;
    }
    
    // Default theme - use local assets
    return imagePath.startsWith('battlefields/')
      ? `/assets/${imagePath}`
      : `/assets/battlefields/${imagePath}`;
  };

  const handlePurchase = async (battlefieldInstanceId, battlefieldName, price, paymentMethod) => {
    if (!isAuthenticated()) {
      warning('Please log in to purchase a battlefield.');
      return;
    }

    if (paymentMethod === 'soulwars' && soulwarsBalance < price) {
      warning(`Insufficient $SOULWARS balance. You have ${Math.round(soulwarsBalance).toLocaleString()}, need ${price.toLocaleString()}`);
      return;
    }

    if (paymentMethod === 'battlefield_token' && battlefieldTokenBalance < 1) {
      warning('Insufficient Battlefield Token balance. You need at least 1 Battlefield Token.');
      return;
    }

    try {
      const response = await fetch(`/api/battlefields/${battlefieldInstanceId}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ paymentMethod })
      });

      const data = await response.json();

      if (data.success) {
        success(`Successfully purchased ${battlefieldName}!`);
        await loadForSaleBattlefields();
        await loadMyBattlefields();
        await loadBalances();
        setActiveTab('my-battlefields');
      } else {
        warning(`Purchase failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error purchasing battlefield:', error);
      warning('Error purchasing battlefield. Please try again.');
    }
  };

  const handleTransferLoot = async (lootEntryIds, quantities, ownerShare = false) => {
    if (!selectedBattlefield) return;

    try {
      const res = await fetch(`/api/battlefields/${selectedBattlefield.id}/loot/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ lootEntryIds, quantities, ownerShare })
      });

      const json = await res.json();
      if (json.success) {
        success(`Transferred ${json.data.transferred.length} loot entries to vault`);
        await loadBattlefieldLoot(selectedBattlefield.id);
        // Refresh summary for list view
        if (selectedBattlefield?.id) {
          await loadBattlefieldLootSummary(selectedBattlefield.id);
        }
      } else {
        warning(`Transfer failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error transferring loot:', error);
      warning('Error transferring loot');
    }
  };

  const handleUpdateName = async (customName) => {
    if (!selectedBattlefield) return;

    try {
      const res = await fetch(`/api/battlefields/${selectedBattlefield.id}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ customName })
      });

      const json = await res.json();
      if (json.success) {
        success('Battlefield name updated');
        await loadMyBattlefields();
        const updated = myBattlefields.find(bf => bf.id === selectedBattlefield.id);
        if (updated) {
          updated.customName = customName;
          setSelectedBattlefield(updated);
        }
      } else {
        warning(`Update failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating name:', error);
      warning('Error updating name');
    }
  };

  const handleUpdateMusic = async (musicTrackKey) => {
    if (!selectedBattlefield) return;

    try {
      const res = await fetch(`/api/battlefields/${selectedBattlefield.id}/music`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ musicTrackKey })
      });

      const json = await res.json();
      if (json.success) {
        success('Battlefield music updated');
        await loadMyBattlefields();
        const updated = myBattlefields.find(bf => bf.id === selectedBattlefield.id);
        if (updated) {
          updated.musicTrackKey = musicTrackKey;
          setSelectedBattlefield(updated);
        }
      } else {
        warning(`Update failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating music:', error);
      warning('Error updating music');
    }
  };

  const handleAddLootPool = async (lootType, themeItemId, tokenSymbol, amount, spawnWeight) => {
    if (!selectedBattlefield) return;

    try {
      const res = await fetch(`/api/battlefields/${selectedBattlefield.id}/loot-pool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ lootType, themeItemId, tokenSymbol, amount, spawnWeight })
      });

      const json = await res.json();
      if (json.success) {
        success('Loot pool entry added');
        await loadBattlefieldLootPool(selectedBattlefield.id);
        // Clear selection after successful add
        setSelectedLootPoolItem(null);
        setSelectedLootPoolToken(null);
      } else {
        warning(`Add failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding to loot pool:', error);
      warning('Error adding to loot pool');
    }
  };

  const handleRemoveLootPool = async (entryId) => {
    if (!selectedBattlefield) return;

    try {
      const res = await fetch(`/api/battlefields/${selectedBattlefield.id}/loot-pool/${entryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const json = await res.json();
      if (json.success) {
        success('Loot pool entry removed');
        await loadBattlefieldLootPool(selectedBattlefield.id);
      } else {
        warning(`Remove failed: ${json.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing from loot pool:', error);
      warning('Error removing from loot pool');
    }
  };

  const battlefields = activeTab === 'my-battlefields' ? myBattlefields : forSaleBattlefields;
  const isLoading = activeTab === 'my-battlefields' ? loading : loadingForSale;

  // Battlefield Detail View
  if (battlefieldView === 'detail' && selectedBattlefield) {
    const themeBf = selectedBattlefield.themeBattlefield || {};
    const displayName = selectedBattlefield.customName || themeBf.name || 'Unknown Battlefield';

    return (
      <div className="min-h-screen bg-gray-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-6">
            <button
              onClick={() => {
                setBattlefieldView('list');
                setSelectedBattlefield(null);
              }}
              className="text-blue-400 hover:text-blue-300 mb-4"
            >
              ← Back to Battlefields
            </button>
            <h1 className="text-3xl font-bold text-white">{displayName}</h1>
          </div>

          {/* Detail Tabs */}
          <div className="mb-6 flex space-x-4 border-b border-gray-700">
            {['overview', 'loot', 'customization', 'loot-pool'].map(tab => (
              <button
                key={tab}
                onClick={() => setBattlefieldDetailTab(tab)}
                className={`px-4 py-2 font-medium transition-colors capitalize ${
                  battlefieldDetailTab === tab
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {battlefieldDetailTab === 'overview' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Loot Team Efficiency</h2>
                <p className="text-gray-400 mb-4">
                  The Loot Team comes out on the field after each battle to clean up leftover loot, tokens, and items. 
                  These stats determine how efficient your Loot Team is at collecting and securing loot for you. 
                  Upgrade these stats to increase the percentage of leftover loot your team collects.
                </p>
                {isAuthenticated() && (
                  <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">💰</span>
                        <span className="text-white font-medium">Your $SOULWARS Balance:</span>
                      </div>
                      <span className="text-2xl font-bold text-green-400">{Math.round(soulwarsBalance).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  { type: 'token', label: 'Token Collection', value: selectedBattlefield.lootTeamTokenEfficiency, icon: '💰' },
                  { type: 'item', label: 'Item Collection', value: selectedBattlefield.lootTeamItemEfficiency, icon: '📦' },
                  { type: 'potion', label: 'Potion Collection', value: selectedBattlefield.lootTeamPotionEfficiency, icon: '🧪' },
                  { type: 'gem', label: 'Gem Collection', value: selectedBattlefield.lootTeamGemEfficiency, icon: '💎' }
                ].map((stat) => (
                  <div key={stat.type} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{stat.icon}</span>
                        <div>
                          <div className="text-sm text-gray-400 mb-1">{stat.label} Efficiency</div>
                          <div className="text-2xl font-bold text-green-400">{formatEfficiency(stat.value)}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => upgradeEfficiency(selectedBattlefield.id, stat.type)}
                        disabled={upgrading[`${selectedBattlefield.id}-${stat.type}`] || stat.value >= 100}
                        className={`px-4 py-2 rounded-lg ${
                          stat.value >= 100
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {upgrading[`${selectedBattlefield.id}-${stat.type}`]
                          ? 'Upgrading...'
                          : stat.value >= 100
                          ? 'Max'
                          : `+1% (${calculateUpgradeCost(stat.value).toFixed(0)} $SOULWARS)`}
                      </button>
                    </div>
                    <div className="mt-3 w-full bg-gray-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(100, stat.value)}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Battlefield ID */}
              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="text-xs text-gray-500 font-mono space-y-1">
                  <div>Battlefield ID: {selectedBattlefield.id}</div>
                  <div>Theme Battlefield ID: {selectedBattlefield.themeBattlefieldId}</div>
                </div>
              </div>
            </div>
          )}

          {/* Loot Tab */}
          {battlefieldDetailTab === 'loot' && battlefieldLoot && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Battlefield Loot</h2>
              
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {/*<div>
                  <h3 className="text-sm text-gray-400 mb-2">Current Remaining</h3>
                  <div className="space-y-1">
                    <div className="text-white">💰 Tokens: {battlefieldLoot.summary.current.tokens}</div>
                    <div className="text-white">📦 Items: {battlefieldLoot.summary.current.items}</div>
                    <div className="text-white">🧪 Potions: {battlefieldLoot.summary.current.potions}</div>
                    <div className="text-white">💎 Gems: {battlefieldLoot.summary.current.gems}</div>
                  </div>
                </div>
                */}
                <div>
                  <h3 className="text-sm text-gray-400 mb-2">Historical Total</h3>
                  <div className="space-y-1">
                    <div className="text-white">💰 Tokens: {battlefieldLoot.summary.historical.tokens}</div>
                    <div className="text-white">📦 Items: {battlefieldLoot.summary.historical.items}</div>
                    <div className="text-white">🧪 Potions: {battlefieldLoot.summary.historical.potions}</div>
                    <div className="text-white">💎 Gems: {battlefieldLoot.summary.historical.gems}</div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm text-gray-400 mb-2">Owner Claimable</h3>
                  <div className="space-y-1">
                    <div className="text-white">💰 Tokens: {battlefieldLoot.summary.ownerKept?.tokens ?? 0}</div>
                    <div className="text-white">📦 Items: {battlefieldLoot.summary.ownerKept?.items ?? 0}</div>
                    <div className="text-white">🧪 Potions: {battlefieldLoot.summary.ownerKept?.potions ?? 0}</div>
                    <div className="text-white">💎 Gems: {battlefieldLoot.summary.ownerKept?.gems ?? 0}</div>
                  </div>
                </div>
              </div>

              {/* Owner Claimable Grid */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">Claimable (Owner Share)</h3>
                <p className="text-gray-400 text-sm mb-4">Click on any item to claim it to your Player Vault</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 xl:grid-cols-9 gap-1">
                  {battlefieldLoot.lootEntries.filter(e => e.ownerKeptAmount > 0).map(entry => {
                    const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';
                    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
                    
                    // Get theme ID from battlefield
                    const themeId = selectedBattlefield?.themeBattlefield?.theme || DEFAULT_THEME_ID;
                    
                    // Determine icon and label
                    let iconUrl = null;
                    let label = entry.themeItemId || entry.tokenSymbol || entry.lootType;
                    let emoji = '📦';
                    
                    if (entry.lootType === 'token') {
                      emoji = '💰';
                      label = entry.tokenSymbol || 'Token';
                    } else if (entry.itemDetails) {
                      // Use item details if available
                      label = entry.itemDetails.name || entry.themeItemId;
                      if (entry.itemDetails.iconKey) {
                        iconUrl = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${themeId}/items/${entry.itemDetails.iconKey}.png`;
                      }
                      // Set emoji based on type
                      const typeEmoji = {
                        healing: '❤️',
                        mana: '💙',
                        poison: '☠️',
                        strength: '💪',
                        defense: '🛡️',
                        speed: '⚡',
                        ammo: '🏹',
                        arrow: '🏹',
                      };
                      emoji = typeEmoji[entry.itemDetails.type] || (entry.lootType === 'potion' ? '🧪' : entry.lootType === 'gem' ? '💎' : '📦');
                    } else {
                      // Fallback emoji
                      emoji = entry.lootType === 'potion' ? '🧪' : entry.lootType === 'gem' ? '💎' : '📦';
                    }
                    
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        onClick={() => {
                          const defaultQty = entry.ownerKeptAmount;
                          const input = window.prompt(`Enter amount to claim (max ${defaultQty})`, `${defaultQty}`);
                          if (input === null) return; // User cancelled
                          const qty = Number(input);
                          if (!Number.isFinite(qty) || qty <= 0 || qty > entry.ownerKeptAmount) {
                            warning(`Invalid amount. Please enter a number between 1 and ${entry.ownerKeptAmount}`);
                            return;
                          }
                          handleTransferLoot([entry.id], [qty], true);
                        }}
                        title={`${label}\nClaimable: ${entry.ownerKeptAmount}\nType: ${entry.lootType}`}
                        className="group relative bg-gray-900/60 border border-gray-700/60 hover:border-blue-500/70 hover:bg-gray-800/80 rounded-md p-1 flex flex-col items-center transition shadow-sm"
                      >
                        <div className="relative w-full pb-[55%] mb-0.5">
                          {iconUrl ? (
                            <>
                              <img
                                src={iconUrl}
                                alt={label}
                                className="absolute inset-0 w-full h-full object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.parentElement?.querySelector('.fallback-emoji');
                                  if (fallback) fallback.classList.remove('hidden');
                                }}
                              />
                              <div className="fallback-emoji hidden absolute inset-0 flex items-center justify-center text-base">
                                {emoji}
                              </div>
                            </>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-base">
                              {emoji}
                            </div>
                          )}
                          {entry.ownerKeptAmount > 1 && (
                            <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-[12px] px-1 py-0.5 rounded-full text-white">
                              ×{entry.ownerKeptAmount}
                            </span>
                          )}
                        </div>
                        <div className="text-[9px] text-gray-200 text-center truncate w-full">
                          {label}
                        </div>
                        {entry.itemDetails?.type && (
                          <div className="text-[7px] text-gray-500 mt-0.5 uppercase tracking-wide">
                            {entry.itemDetails.type}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {battlefieldLoot.lootEntries.filter(e => e.ownerKeptAmount > 0).length === 0 && (
                    <div className="text-gray-400 text-sm col-span-full text-center py-8">No claimable owner loot yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Customization Tab */}
          {battlefieldDetailTab === 'customization' && (
            <div className="bg-gray-800 rounded-lg p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Battlefield Name</label>
                <input
                  type="text"
                  defaultValue={selectedBattlefield.customName || ''}
                  maxLength={50}
                  placeholder={themeBf.name || 'Enter custom name'}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  onBlur={(e) => {
                    const newName = e.target.value.trim();
                    if (newName && newName !== selectedBattlefield.customName) {
                      handleUpdateName(newName);
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Max 50 characters, no emojis</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Battle Music</label>
                <select
                  value={selectedBattlefield.musicTrackKey || ''}
                  onChange={(e) => handleUpdateMusic(e.target.value || null)}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                >
                  <option value="">Default (Theme Music)</option>
                  {battlefieldMusic.map(track => (
                    <option key={track.track_key} value={track.track_key}>
                      {track.title || track.track_key} {track.artist ? `- ${track.artist}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Loot Pool Tab */}
          {battlefieldDetailTab === 'loot-pool' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Custom Loot Pool</h2>
              <p className="text-gray-400 mb-4">Add items from your vault and tokens from your ledger balance that will spawn as supplemental loot on this battlefield.</p>
              
              {/* User Vault Items Grid */}
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Your Vault Items (Click to Add)</h3>
                {loadingInventory ? (
                  <div className="text-gray-400 text-center py-4">Loading inventory...</div>
                ) : userInventory.length === 0 ? (
                  <div className="text-gray-400 text-center py-4">No items in your vault</div>
                ) : (
                  <LootPoolItemGrid 
                    items={userInventory}
                    onItemClick={(item) => {
                      setSelectedLootPoolItem(item);
                      setSelectedLootPoolToken(null);
                      // Scroll to form
                      setTimeout(() => {
                        const form = document.querySelector('[data-loot-pool-form]');
                        if (form) {
                          form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }
                      }, 100);
                    }}
                  />
                )}
              </div>

              {/* Token Balances */}
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Your Token Balances (Click to Add)</h3>
                {Object.keys(tokenBalances).length === 0 ? (
                  <div className="text-gray-400 text-center py-4">No token balances available</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(tokenBalances).map(([symbol, balance]) => (
                      <button
                        key={symbol}
                        type="button"
                        onClick={() => {
                          setSelectedLootPoolToken(symbol);
                          setSelectedLootPoolItem(null);
                          // Scroll to form
                          setTimeout(() => {
                            const form = document.querySelector('[data-loot-pool-form]');
                            if (form) {
                              form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }
                          }, 100);
                        }}
                        className="bg-gray-600 hover:bg-gray-500 rounded-lg p-3 text-left transition-colors"
                      >
                        <div className="text-white font-medium">{symbol.toUpperCase()}</div>
                        <div className="text-gray-300 text-sm">{balance.toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Loot Pool Entry Form */}
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Add to Loot Pool</h3>
                <LootPoolForm 
                  onSubmit={handleAddLootPool}
                  prefillItem={selectedLootPoolItem}
                  prefillToken={selectedLootPoolToken}
                />
              </div>

              {/* Loot Pool Entries */}
              <div className="space-y-2">
                {battlefieldLootPool.map(entry => (
                  <div key={entry.id} className="bg-gray-700 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <span className="font-medium text-white">{entry.lootType}</span>
                      {' '}
                      {entry.themeItemId || entry.tokenSymbol}
                      {' '}
                      <span className="text-gray-400">x{entry.amount} (weight: {entry.spawnWeight})</span>
                    </div>
                    <button
                      onClick={() => handleRemoveLootPool(entry.id)}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {battlefieldLootPool.length === 0 && (
                  <p className="text-gray-400 text-center py-8">No loot pool entries yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            {isAuthenticated() ? 'Battlefield Management' : 'Battlefields'}
          </h1>
          <p className="text-gray-400">
            {isAuthenticated() 
              ? `Manage your battlefields and upgrade Loot Team efficiency to collect more loot from battles. To add $SOULWARS to your account, visit the Account page.`  
              : 'Browse available battlefields for purchase. Log in to purchase and manage your own battlefields.'}
          </p>
          {isAuthenticated() && (
            <div className="mt-4 text-sm text-gray-400">
              <span>💰 $SOULWARS: {Math.round(soulwarsBalance).toLocaleString()}</span>
              {' '}
              <span>🎫 Battlefield Tokens: {battlefieldTokenBalance}</span>
            </div>
          )}
        </div>

        {isAuthenticated() && (
          <div className="mb-6 flex space-x-4 border-b border-gray-700">
            <button
              onClick={() => setActiveTab('my-battlefields')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'my-battlefields'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              My Battlefields ({myBattlefields.length})
            </button>
            <button
              onClick={() => setActiveTab('for-sale')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'for-sale'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              For Sale ({forSaleBattlefields.length})
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-white text-xl">Loading Battlefields...</div>
          </div>
        ) : battlefields.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <p className="text-gray-300 text-lg mb-2">
              {activeTab === 'my-battlefields'
                ? 'You don\'t own any battlefields yet.'
                : 'No battlefields available for purchase at this time.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {battlefields.map((bf) => {
              const themeBf = bf.themeBattlefield || {};
              const price = bf.priceSoulwars || 1000000;
              const isOwned = isAuthenticated() && bf.ownerUserId === user?.id;
              const displayName = bf.customName || themeBf.name || 'Unknown Battlefield';
              
              return (
                <div key={bf.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 mb-6">
                    {themeBf.image && (
                      <div className="flex-shrink-0">
                        <img
                          src={formatBattlefieldImage(themeBf.image, themeBf.theme || bf.themeBattlefield?.theme)}
                          alt={displayName}
                          className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg border border-gray-700 shadow-lg"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-2">{displayName}</h3>
                      <div className="text-xs text-gray-500 mb-2 font-mono">ID: {bf.id}</div>
                      {themeBf.description && (
                        <p className="text-gray-400 mb-4">{themeBf.description}</p>
                      )}
                      <div className="text-sm text-gray-500">
                        Created: {new Date(bf.createdAt).toLocaleDateString()}
                      </div>
                      {!isOwned && (
                        <div className="mt-4">
                          <div className="text-2xl font-bold text-green-400 mb-3">
                            {price.toLocaleString()} $SOULWARS
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <button
                              onClick={() => handlePurchase(bf.id, displayName, price, 'soulwars')}
                              disabled={soulwarsBalance < price}
                              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                soulwarsBalance < price
                                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              Purchase with $SOULWARS
                            </button>
                            {battlefieldTokenBalance >= 1 && (
                              <button
                                onClick={() => handlePurchase(bf.id, displayName, price, 'battlefield_token')}
                                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                              >
                                Purchase with Battlefield Token
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {isOwned && (
                        <button
                          onClick={() => {
                            setSelectedBattlefield(bf);
                            setBattlefieldView('detail');
                            setBattlefieldDetailTab('overview');
                          }}
                          className="mt-4 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Manage Battlefield
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Loot Totals */}
                  {isOwned && battlefieldLootSummaries[bf.id] && (
                    <div className="border-t border-gray-700 pt-4 mb-4">
                      <h4 className="text-lg font-semibold text-white mb-3">Total Loot</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">💰</span>
                            <div className="text-xs text-gray-400">Tokens</div>
                          </div>
                          <div className="text-lg font-bold text-white">
                            {battlefieldLootSummaries[bf.id].ownerKept?.tokens || 0}
                          </div>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">📦</span>
                            <div className="text-xs text-gray-400">Items</div>
                          </div>
                          <div className="text-lg font-bold text-white">
                            {battlefieldLootSummaries[bf.id].ownerKept?.items || 0}
                          </div>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">🧪</span>
                            <div className="text-xs text-gray-400">Potions</div>
                          </div>
                          <div className="text-lg font-bold text-white">
                            {battlefieldLootSummaries[bf.id].ownerKept?.potions || 0}
                          </div>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">💎</span>
                            <div className="text-xs text-gray-400">Gems</div>
                          </div>
                          <div className="text-lg font-bold text-white">
                            {battlefieldLootSummaries[bf.id].ownerKept?.gems || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-700 pt-6">
                    <h4 className="text-xl font-semibold text-white mb-4">Loot Team Efficiency</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { type: 'token', label: 'Tokens', value: bf.lootTeamTokenEfficiency, icon: '💰' },
                        { type: 'item', label: 'Items', value: bf.lootTeamItemEfficiency, icon: '📦' },
                        { type: 'potion', label: 'Potions', value: bf.lootTeamPotionEfficiency, icon: '🧪' },
                        { type: 'gem', label: 'Gems', value: bf.lootTeamGemEfficiency, icon: '💎' }
                      ].map((stat) => (
                        <div key={stat.type} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl">{stat.icon}</span>
                            <div>
                              <div className="text-sm text-gray-400">{stat.label}</div>
                              <div className="text-2xl font-bold text-green-400">{formatEfficiency(stat.value)}</div>
                            </div>
                          </div>
                          <div className="w-full bg-gray-600 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min(100, stat.value)}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Loot Pool Form Component
function LootPoolForm({ onSubmit, prefillItem, prefillToken }) {
  const [lootType, setLootType] = useState(prefillToken ? 'token' : (prefillItem ? 'item' : 'token'));
  const [themeItemId, setThemeItemId] = useState(prefillItem?.itemId || prefillItem?.item_id || '');
  const [tokenSymbol, setTokenSymbol] = useState(prefillToken || 'soulwars');
  const [amount, setAmount] = useState('');
  const [spawnWeight, setSpawnWeight] = useState('1');

  // Update form when prefill values change
  useEffect(() => {
    if (prefillItem) {
      setLootType('item');
      setThemeItemId(prefillItem.itemId || prefillItem.item_id || '');
    } else if (prefillToken) {
      setLootType('token');
      setTokenSymbol(prefillToken);
    }
  }, [prefillItem, prefillToken]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (lootType === 'token' && !tokenSymbol) {
      alert('Token symbol is required');
      return;
    }
    if (lootType === 'item' && !themeItemId) {
      alert('Theme item ID is required');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      alert('Amount must be a positive number');
      return;
    }
    onSubmit(lootType, themeItemId || null, tokenSymbol || null, parseFloat(amount), parseInt(spawnWeight) || 1);
    // Reset form
    setThemeItemId('');
    setTokenSymbol('soulwars');
    setAmount('');
    setSpawnWeight('1');
    // Clear prefill (will be handled by parent component)
  };

  return (
    <form data-loot-pool-form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Loot Type</label>
        <select
          name="lootType"
          value={lootType}
          onChange={(e) => setLootType(e.target.value)}
          className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg"
        >
          <option value="token">Token</option>
          <option value="item">Item</option>
        </select>
      </div>

      {lootType === 'token' ? (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Token Symbol</label>
          <input
            name="tokenSymbol"
            type="text"
            value={tokenSymbol}
            onChange={(e) => setTokenSymbol(e.target.value)}
            placeholder="soulwars"
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg"
            required
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Theme Item ID</label>
          <input
            name="themeItemId"
            type="text"
            value={themeItemId}
            onChange={(e) => setThemeItemId(e.target.value)}
            placeholder="potion_heal_t1"
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg"
            required
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Amount</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="100"
          min="0.000000000000000001"
          step="0.000000000000000001"
          className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Spawn Weight</label>
        <input
          type="number"
          value={spawnWeight}
          onChange={(e) => setSpawnWeight(e.target.value)}
          placeholder="1"
          min="1"
          className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg"
          required
        />
        <p className="text-xs text-gray-500 mt-1">Higher weight = more likely to spawn</p>
      </div>

      <button
        type="submit"
        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
      >
        Add to Loot Pool
      </button>
    </form>
  );
}

// Loot Pool Item Grid Component
function LootPoolItemGrid({ items, onItemClick }) {
  const DEFAULT_THEME_ID = process.env.REACT_APP_DEFAULT_THEME_ID || 'soulwars-default';
  
  const formatItemIcon = (themeId, iconKey) => {
    if (!iconKey) return null;
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const resolvedTheme = themeId || DEFAULT_THEME_ID;
    if (!resolvedTheme) return null;
    return `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${resolvedTheme}/items/${iconKey}.png`;
  };

  const TYPE_EMOJI = {
    healing: '❤️',
    mana: '💙',
    poison: '☠️',
    strength: '💪',
    defense: '🛡️',
    speed: '⚡',
    ammo: '🏹',
    arrow: '🏹',
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 xl:grid-cols-9 gap-1">
      {items.map((item, idx) => {
        const quantity = Number(item.quantity ?? item.qty ?? 0) || 0;
        const iconKey = item.iconKey ?? item.icon_key ?? null;
        const iconUrl = formatItemIcon(item.themeId || item.theme_id || DEFAULT_THEME_ID, iconKey);
        const emoji = TYPE_EMOJI[item.type || item.itemType] || '📦';
        const itemId = item.itemId || item.item_id || '';
        const itemName = item.name || itemId;

        return (
          <button
            key={item.id || `${itemId}-${idx}`}
            type="button"
            onClick={() => onItemClick && onItemClick(item)}
            title={`${itemName}\nQuantity: ${quantity}\nType: ${item.type || item.itemType || 'Unknown'}`}
            className="group relative bg-gray-900/60 border border-gray-700/60 hover:border-blue-500/70 hover:bg-gray-800/80 rounded-md p-1 flex flex-col items-center transition shadow-sm"
          >
            <div className="relative w-full pb-[55%] mb-0.5">
              {iconUrl ? (
                <>
                  <img
                    src={iconUrl}
                    alt={itemName}
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.parentElement?.querySelector('.fallback-emoji');
                      if (fallback) fallback.classList.remove('hidden');
                    }}
                  />
                  <div className="fallback-emoji hidden absolute inset-0 flex items-center justify-center text-base">
                    {emoji}
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-base">
                  {emoji}
                </div>
              )}
              {quantity > 1 && (
                <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-[12px] px-1 py-0.5 rounded-full text-white">
                  ×{quantity}
                </span>
              )}
            </div>
            <div className="text-[9px] text-gray-200 text-center truncate w-full">
              {itemName}
            </div>
            {item.type && (
              <div className="text-[7px] text-gray-500 mt-0.5 uppercase tracking-wide">
                {item.type}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
